import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as PMNode } from "@tiptap/pm/model";

import { defaultBrowserImageResolver } from "utils/pdf";
import { computeCoverPageBreaks, CoverPageBreak } from "utils/pdf/page-breaks";
import { store } from "utils/store";

// Draws "Page N" markers in the editor at the exact points where the cover page
// overflows to a new page — computed by running the SAME pdfmake converter the
// server uses (see utils/pdf/page-breaks). pdfmake owns pagination; this just
// visualises its decisions in the editor.

export const pageBoundaryKey = new PluginKey<DecorationSet>("pageBoundary");

const RECOMPUTE_DELAY_MS = 600;

function makeMarker(page: number): HTMLElement {
  const el = document.createElement("div");
  el.contentEditable = "false";
  el.className = "page-boundary";
  el.style.cssText =
    "position:relative; height:0; border-top:2px dashed #e0914c; margin:16px 0; user-select:none; pointer-events:none;";

  const label = document.createElement("span");
  label.textContent = `Page ${page}`;
  label.style.cssText =
    "position:absolute; right:0; top:-9px; background:#e0914c; color:#fff; font-size:10px; font-weight:700; letter-spacing:.08em; padding:1px 6px; border-radius:3px;";

  el.appendChild(label);
  return el;
}

function buildDecorations(doc: PMNode, breaks: CoverPageBreak[]): DecorationSet {
  if (!breaks.length) return DecorationSet.empty;

  // Record each top-level node's start offset + size so we can place markers
  // either before a node (block-aligned) or inside it (word-level).
  const nodeOffsets: { offset: number; size: number }[] = [];
  doc.forEach((node, offset) => nodeOffsets.push({ offset, size: node.nodeSize }));

  const decorations: Decoration[] = [];

  for (const b of breaks) {
    const node = nodeOffsets[b.nodeIndex];
    if (!node) continue;

    let pos: number;
    if (typeof b.charOffset === "number") {
      // Inside the node at the word boundary. content starts at offset+1; clamp
      // to stay within the node.
      pos = Math.min(node.offset + 1 + b.charOffset, node.offset + node.size - 1);
    } else {
      // Block-aligned: before the node.
      pos = node.offset;
    }

    decorations.push(
      Decoration.widget(pos, () => makeMarker(b.page), { side: -1, key: `pb-${b.nodeIndex}-${b.charOffset ?? "block"}-${b.page}` })
    );
  }

  return DecorationSet.create(doc, decorations);
}

export const PageBoundary = Extension.create({
  name: "pageBoundary",

  addProseMirrorPlugins() {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastComputedJson = "";

    return [
      new Plugin<DecorationSet>({
        key: pageBoundaryKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, old) {
            const meta = tr.getMeta(pageBoundaryKey) as CoverPageBreak[] | undefined;
            if (meta) return buildDecorations(tr.doc, meta);
            return old.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return pageBoundaryKey.getState(state);
          },
        },
        view(view) {
          const recompute = () => {
            const content = view.state.doc.toJSON().content ?? [];
            const json = JSON.stringify(content);
            if (json === lastComputedJson) return;
            lastComputedJson = json;

            const { imageManifest, storageToken } = store.getState().templateBuilderSlice;
            computeCoverPageBreaks(content as any, { resolveImage: defaultBrowserImageResolver, imageManifest, storageToken })
              .then((breaks) => {
                // Stale guard: skip if the doc changed while we were computing.
                if (JSON.stringify(view.state.doc.toJSON().content ?? []) !== json) return;
                view.dispatch(
                  view.state.tr
                    .setMeta(pageBoundaryKey, breaks)
                    .setMeta("addToHistory", false)
                    .setMeta("preventUpdate", true) // decoration-only: don't trigger onUpdate/save
                );
              })
              .catch((ex) => {
                // eslint-disable-next-line no-console
                console.error("page-boundary compute failed", ex);
              });
          };

          const schedule = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(recompute, RECOMPUTE_DELAY_MS);
          };

          schedule(); // initial content

          return {
            update(updatedView, prevState) {
              if (!updatedView.state.doc.eq(prevState.doc)) schedule();
            },
            destroy() {
              if (timer) clearTimeout(timer);
            },
          };
        },
      }),
    ];
  },
});

export default PageBoundary;
